import { isApiError } from '@pa/api'
import { useGetClinic } from '@pa/api/admin'
import type { ErrorsSearch } from '@pa/staff'
import { useToast } from '@pa/ui'
import { Link } from '@tanstack/react-router'
import { Pencil } from 'lucide-react'
import { useEffect, useState } from 'react'
import { ActivityTab } from '@/features/activity'
import { StatusPill, TestingBadge } from '@/features/clinics'
import { ActAsClinicAdmin, forgetStartedImpersonation } from '@/features/impersonation'
import { ProtectionTab, type ProtectionSearch } from '@/features/protection'
import { CLINIC_TABS, TAB_LABELS, type ClinicTab } from '../model/tabs'
import { ConnectionTab } from './ConnectionTab'
import { EditClinicModal } from './EditClinicModal'
import { ErrorsTab } from './ErrorsTab'
import { HealthTab } from './HealthTab'
import { StatusAction } from './StatusAction'
import { SummaryTab } from './SummaryTab'
import { UsersTab } from './UsersTab'

/** S-04's search params (Frontend §7.1): each tab's, plus `notice` (`impersonation-ended`, S-06 return). */
export type ClinicDetailSearch = ErrorsSearch & {
  actor?: string
  decision?: ProtectionSearch['decision']
  notice?: 'impersonation-ended'
}

export interface ClinicDetailPageProps {
  clinicId: string
  tab: ClinicTab
  /**
   * The Errors tab's filters, page and drawer (as C-03), the Health tab's incident page, the Activity tab's person,
   * date and page (Frontend §7.1), and `notice`.
   */
  search?: ClinicDetailSearch
  onSearch?: (next: ClinicDetailSearch, opts?: { replace?: boolean }) => void
}

export const IMPERSONATION_ENDED_NOTICE = 'Impersonation ended'

/**
 * S-04 Clinic detail (UserFlows S-04, Frontend §7.2): header (name, ID, status, Act as clinic admin, Edit,
 * Deactivate / Reactivate) and its tabs.
 */
export function ClinicDetailPage({ clinicId, tab, search = {}, onSearch }: ClinicDetailPageProps) {
  const query = useGetClinic(clinicId, { query: { staleTime: 30_000 } })
  const [editing, setEditing] = useState(false)
  const toast = useToast()
  const notice = search.notice

  // Back from the clinic app (Exit or expiry, Frontend §7.3 step 5): an info toast, then the param goes.
  useEffect(() => {
    if (notice !== 'impersonation-ended') return
    forgetStartedImpersonation()
    toast(IMPERSONATION_ENDED_NOTICE, 'info')
    const next = { ...search }
    delete next.notice
    onSearch?.(next, { replace: true })
    // Once per arrival with the notice.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notice])

  if (query.isPending) {
    return (
      <div className="card card-pad" role="status">
        Loading the clinic…
      </div>
    )
  }
  if (query.isError) {
    const gone = isApiError(query.error) && query.error.code === 'not_found'
    return (
      <div className="card card-pad form-grid" role="alert">
        <h1>{gone ? 'This clinic no longer exists' : "Couldn't load the clinic"}</h1>
        {gone ? (
          <Link to="/clinics" className="btn btn-secondary btn-sm">
            Back to clinics
          </Link>
        ) : (
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => void query.refetch()}>
            Retry
          </button>
        )}
      </div>
    )
  }

  const clinic = query.data
  return (
    <>
      <div className="page-head">
        <div>
          <div className="detail-head">
            <h1>{clinic.name}</h1>
            <StatusPill status={clinic.displayStatus} />
            <TestingBadge on={clinic.testingIgnorePfHours} />
          </div>
          <p>
            Clinic ID <span className="mono">{clinic.slug}</span>
          </p>
        </div>
        <div className="actions">
          <ActAsClinicAdmin clinic={clinic} />
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => {
              setEditing(true)
            }}
          >
            <Pencil aria-hidden /> Edit
          </button>
          <StatusAction clinic={clinic} />
        </div>
      </div>
      <nav className="top-tabs" aria-label="Clinic sections">
        {CLINIC_TABS.map((t) => (
          <Link
            key={t}
            to="/clinics/$clinicId/$tab"
            params={{ clinicId, tab: t }}
            className="top-tab"
            aria-current={t === tab ? 'page' : undefined}
          >
            {TAB_LABELS[t]}
          </Link>
        ))}
      </nav>
      {tab === 'summary' && <SummaryTab clinic={clinic} />}
      {tab === 'users' && <UsersTab clinicId={clinicId} />}
      {tab === 'connection' && <ConnectionTab clinicId={clinicId} />}
      {tab === 'protection' && (
        <ProtectionTab
          clinicId={clinicId}
          timeZone={clinic.timeZone}
          search={search}
          onSearch={(next, opts) => {
            onSearch?.(next, opts)
          }}
        />
      )}
      {tab === 'health' && (
        <HealthTab
          clinicId={clinicId}
          timeZone={clinic.timeZone}
          page={search.page ?? 1}
          onPage={(page) => {
            onSearch?.(page > 1 ? { page } : {})
          }}
        />
      )}
      {tab === 'errors' && (
        <ErrorsTab
          clinicId={clinicId}
          timeZone={clinic.timeZone}
          search={search}
          onSearch={(next, opts) => {
            onSearch?.(next, opts)
          }}
        />
      )}
      {tab === 'activity' && (
        <ActivityTab
          clinicId={clinicId}
          timeZone={clinic.timeZone}
          search={search}
          onSearch={(next, opts) => {
            onSearch?.(next, opts)
          }}
        />
      )}
      {editing && (
        <EditClinicModal
          clinic={clinic}
          onClose={() => {
            setEditing(false)
          }}
        />
      )}
    </>
  )
}
